import { Inject, Injectable } from '@angular/core';
import { Params, Router } from '@angular/router';
import { APP_CONFIG } from '@mnz-core/injectors/app-configuration';
import { AccountType, MessageType } from '@mnz-core/models/_enums';
import { IAppConfiguration } from '@mnz-core/models/app-configuration';
import { IResponse } from '@mnz-core/models/response';
import { IRootState } from '@mnz-core/modules/store/models/root-state';
import * as routerSelectors from '@mnz-core/modules/store/selectors/router.selectors';
import { HttpClientService } from '@mnz-core/services/http-client.service';
import * as accountActions from '@mnz-modules/login/store/actions/account.actions';
import * as appActions from '@mnz-store/actions/app.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

@Injectable()
export class AccountEffects {
  constructor(
    @Inject(APP_CONFIG) private readonly _config: IAppConfiguration,
    private readonly _actions$: Actions,
    private readonly _store$: Store<IRootState>,
    private readonly _http: HttpClientService,
    private readonly _router: Router
  ) {}

  resetAccount$ = createEffect(() =>
    this._actions$.pipe(
      ofType(accountActions.resetAccount),
      switchMap(({ email, accountType }) => {
        return this._http
          .post<IResponse<any>>(`${this._config.url_api_base}/mnz-auth-api/reset`, {
            email,
            accountType,
          })
          .pipe(
            map(() => {
              const label = `LOGIN.LOST_CREDENTIALS.RESET_ACCOUNT.${
                accountType === AccountType.CARD_HOLDER ? 'CARDHOLDER.LABEL' : 'CONTACTPERSON.LABEL'
              }`;
              const body = `LOGIN.LOST_CREDENTIALS.RESET_ACCOUNT.${
                accountType === AccountType.CARD_HOLDER ? 'CARDHOLDER.BODY' : 'CONTACTPERSON.BODY'
              }`;
              return accountActions.resetAccountSuccess({
                message: {
                  label,
                  body,
                  translateParams: { email },
                  messageType: MessageType.RESET_ACCOUNT_SUCCESS,
                },
              });
            }),
            catchError((error) => {
              return of(
                appActions.addMessage({
                  message: {
                    label: 'LOGIN.SERVER_ERROR.RESET_ACCOUNT.LABEL',
                    error,
                  },
                  action: accountActions.resetAccountFail(),
                })
              );
            })
          );
      })
    )
  );

  resetAccountSuccess$ = createEffect(() =>
    this._actions$.pipe(
      ofType(accountActions.resetAccountSuccess),
      withLatestFrom(
        this._store$.pipe(
          select(routerSelectors.getParams),
          map<Params, AccountType>(({ account }) => account)
        )
      ),
      tap(([, account]) => this._router.navigate([`/inloggen/${account}`], { state: { keepMessage: true } })),
      map(([{ message }]) => appActions.addMessage({ message }))
    )
  );

  activateAccount$ = createEffect(() =>
    this._actions$.pipe(
      ofType(accountActions.activateAccount),
      switchMap(({ email }) => {
        return this._http
          .post<IResponse<any>>(`${this._config.url_api_base}/mnz-auth-api/send-activation-link`, { email })
          .pipe(
            map(() =>
              accountActions.activateAccountSuccess({
                message: {
                  body: 'LOGIN.LOST_CREDENTIALS.RESET_ACCOUNT.CARDHOLDER.BODY',
                  label: 'LOGIN.LOST_CREDENTIALS.RESET_ACCOUNT.CARDHOLDER.LABEL',
                  translateParams: { email },
                  messageType: MessageType.ACTIVATE_SUCCESS,
                },
              })
            ),
            catchError((error) => {
              return of(
                appActions.addMessage({
                  message: {
                    label: 'LOGIN.SERVER_ERROR.ACTIVATE_ACCOUNT.LABEL',
                    body: 'LOGIN.SERVER_ERROR.ACTIVATE_ACCOUNT.BODY',
                    error,
                  },
                  action: accountActions.activateAccountFail(),
                })
              );
            })
          );
      })
    )
  );

  activateAccountSuccess$ = createEffect(() =>
    this._actions$.pipe(
      ofType(accountActions.activateAccountSuccess),
      tap(() => this._router.navigate([`/`], { state: { keepMessage: true } })),
      map(({ message }) => appActions.addMessage({ message }))
    )
  );
}
